import { FormControl, Validators } from '@angular/forms';
import {
  EoCustomControlConfig,
  EoDatepickerConfig,
  EoDropdownConfig,
  EoFormPanelConfig,
  EoInputConfig,
} from 'ngx-eo-web-components';
import { amkaValidator } from '../../core/validators/amka.validator';
import { surgeryStatuses } from '../../shared/components/surgery-status-dropdown/surgery-status-dropdown.config';
import { is } from 'date-fns/locale';

const modifiedSurgeryStatuses = surgeryStatuses.filter(
  (x) =>
    x.value !== 'for_assignment' &&
    x.value !== 'inactive' &&
    x.value !== 'noteligible' &&
    x.value !== 'communication_failed' &&
    x.value !== 'toprivate'
);

export const assignedSurgerySearchForm = new EoFormPanelConfig({
  name: 'form',
  panelContentStyleClass: 'grid',
  config: [
    new EoCustomControlConfig({
      name: 'hospitalID',
      label: 'Νοσοκομείο',
      styleClass: 'col-6',
      control: new FormControl(null, [Validators.required]),
    }),
    new EoCustomControlConfig({
      name: 'clinicID',
      label: 'Κλινική',
      styleClass: 'col-6',
      control: new FormControl(null),
    }),
    new EoCustomControlConfig({
      name: 'hospitalAvailabilityID',
      styleClass: 'col-6',
      label: 'Μήνας Ανάθεσης',
      control: new FormControl(null),
    }),
    new EoInputConfig({
      name: 'patientAmka',
      styleClass: 'col-6',
      type: 'text',
      label: 'ΑΜΚΑ Ασθενούς',
      placeholder: 'Παρακαλώ συμπληρώστε το ΑΜΚΑ Ασθενούς',
      control: new FormControl(null),
    }),
    new EoInputConfig({
      name: 'uniqueListPatientCode',
      styleClass: 'col-6',
      type: 'text',
      label: 'Κωδικός ΕΛΧ',
      placeholder: 'Παρακαλώ συμπληρώστε τον κωδικό ΕΛΧ',
      control: new FormControl(null),
    }),
    new EoDropdownConfig({
      name: 'status',
      label: 'Κατάσταση',
      placeholder: 'Επιλέξτε κατάσταση',
      styleClass: 'col-6',
      options: modifiedSurgeryStatuses,
      optionLabel: 'label',
      optionValue: 'value',
      appendTo: 'body',
      control: new FormControl(null),
      showClear: true,
    }),
  ],
});

// ViewOnly form: same as above but without patientAmka field
export const assignedSurgerySearchFormViewOnly = new EoFormPanelConfig({
  name: 'form',
  panelContentStyleClass: 'grid',
  config: [
    new EoCustomControlConfig({
      name: 'hospitalID',
      label: 'Νοσοκομείο',
      styleClass: 'col-6',
      control: new FormControl(null),
    }),
    new EoCustomControlConfig({
      name: 'clinicID',
      label: 'Κλινική',
      styleClass: 'col-6',
      control: new FormControl(null),
    }),
    new EoInputConfig({
      name: 'uniqueListPatientCode',
      styleClass: 'col-6',
      type: 'text',
      label: 'Κωδικός ΕΛΧ',
      placeholder: 'Παρακαλώ συμπληρώστε τον κωδικό ΕΛΧ',
      control: new FormControl(null),
    }),
    new EoDropdownConfig({
      name: 'status',
      label: 'Κατάσταση',
      placeholder: 'Επιλέξτε κατάσταση',
      styleClass: 'col-6',
      options: modifiedSurgeryStatuses,
      optionLabel: 'label',
      optionValue: 'value',
      appendTo: 'body',
      control: new FormControl(null),
      showClear: true,
    }),
  ],
});

export const assignedSurgerySearchFormConfig: EoFormPanelConfig[] = [
  assignedSurgerySearchForm,
];

export const assignedSurgerySearchFormViewOnlyConfig: EoFormPanelConfig[] = [
  assignedSurgerySearchFormViewOnly,
];

const setFc = (config: EoFormPanelConfig[], formGroupName: string, formControlName: string, value: any) => {
  config
    .find((cfc) => cfc.name === formGroupName)
    ?.config?.find((c) => c.name === formControlName)
    ?.control?.setValue(value);
};

const disableFc = (config: EoFormPanelConfig[], formGroupName: string, formControlName: string) => {
  config
    .find((cfc) => cfc.name === formGroupName)
    ?.config?.find((c) => c.name === formControlName)
    ?.control?.disable();
};

const enableFc = (config: EoFormPanelConfig[], formGroupName: string, formControlName: string) => {
  config
    .find((cfc) => cfc.name === formGroupName)
    ?.config?.find((c) => c.name === formControlName)
    ?.control?.enable();
};

export const initSurgerySearchForm = (
  hospitalId?: string | null,
  clinicId?: string | null,
  isClinicAdmin: boolean = false,
  isViewOnly: boolean = false
): void => {
  const cfg = isViewOnly ? assignedSurgerySearchFormViewOnlyConfig : assignedSurgerySearchFormConfig;
  initHospitalAndClinicDP(cfg, hospitalId, clinicId);
  handleHospitalFc(cfg, hospitalId, isViewOnly);
  handleClinicFc(cfg, clinicId, isClinicAdmin);
};

function handleClinicFc(
  config: EoFormPanelConfig[],
  clinicId: string | null | undefined,
  isClinicAdmin = false
) {
  if (clinicId && isClinicAdmin) {
    disableFc(config, 'form', 'clinicID');
  } else {
    enableFc(config, 'form', 'clinicID');
  }
}

function handleHospitalFc(
  config: EoFormPanelConfig[],
  hospitalId: string | null | undefined,
  isViewOnly: boolean
) {
  // ViewOnly: hospitalID is optional, no disable/enable forcing
  if (!isViewOnly && hospitalId) {
    disableFc(config, 'form', 'hospitalID');
  } else if (!isViewOnly) {
    enableFc(config, 'form', 'hospitalID');
  }
}

function initHospitalAndClinicDP(
  config: EoFormPanelConfig[],
  hospitalId: string | null | undefined,
  clinicId: string | null | undefined
) {
  setFc(config, 'form', 'hospitalID', hospitalId);
  setFc(config, 'form', 'clinicID', clinicId);
}

export function setSearchParams(savedSearchParams: any, isViewOnly: boolean = false) {
  const cfg = isViewOnly ? assignedSurgerySearchFormViewOnlyConfig : assignedSurgerySearchFormConfig;
  setFc(cfg, 'form', 'hospitalAvailabilityID', savedSearchParams.hospitalAvailabilityID);
  if (!isViewOnly) {
    setFc(cfg, 'form', 'patientAmka', savedSearchParams.patientAmka);
  }
  setFc(cfg, 'form', 'status', savedSearchParams.status);
  setFc(cfg, 'form', 'uniqueListPatientCode', savedSearchParams.uniqueListPatientCode);
}

export const makeHospitalOptional = (): void => {
  const control = assignedSurgerySearchFormViewOnlyConfig
    .find((cfc) => cfc.name === 'form')
    ?.config?.find((c) => c.name === 'hospitalID')
    ?.control;
  if (control) {
    control.clearValidators();
    control.updateValueAndValidity();
  }
};

export const makeHospitalRequired = (): void => {
  const control = assignedSurgerySearchFormConfig
    .find((cfc) => cfc.name === 'form')
    ?.config?.find((c) => c.name === 'hospitalID')
    ?.control;
  if (control) {
    control.setValidators([Validators.required]);
    control.updateValueAndValidity();
  }
};
